import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/firebase';
import { useNavigate, useLocation } from 'react-router-dom';

import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { measuredGet } from '../utils/readMeter';
import HostLayout from '../layouts/HostLayout';
import { PRICING } from '../constants/pricing';

const Marketplace = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previouslyPurchasedGames, setPreviouslyPurchasedGames] = useState(new Set());
  const [purchaseLoading, setPurchaseLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState('all');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'browse', or 'games'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [officialGames, setOfficialGames] = useState([]);
  const [loadingOfficialGames, setLoadingOfficialGames] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewGame, setPreviewGame] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check URL params for view mode
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const viewParam = urlParams.get('view');
    if (viewParam && ['calendar', 'browse', 'games'].includes(viewParam)) {
      setViewMode(viewParam);
    }
  }, [location.search]);

  useEffect(() => {
    loadGames();
    if (viewMode === 'calendar') {
      loadOfficialGames();
    }
  }, [viewMode]);

  useEffect(() => {
    if (user) {
      loadPreviouslyPurchasedGames();
    }
  }, [user]);

  useEffect(() => {
    if (viewMode === 'calendar') {
      loadOfficialGames();
    }
  }, [currentMonth, viewMode]);

  const loadGames = async () => {
    try {
      console.log('🛒 MARKETPLACE: Loading games...');
      
      // Load from the marketplace collection first
      const gamesSnapshot = await measuredGet(
        () => getDocs(collection(db, 'games', 'content', 'triviaGames')),
        'marketplace/games',
        window.location.pathname
      );
      console.log('🛒 MARKETPLACE: Found', gamesSnapshot.size, 'games in marketplace collection');
      
      const gamesData = gamesSnapshot.docs.map(doc => {
        try {
          return {
            id: doc.id,
            ...doc.data()
          };
        } catch (error) {
          console.error('Error processing marketplace game data for doc', doc.id, ':', error);
          return null;
        }
      }).filter(game => {
        if (!game) return false;
        
        // Filter for approved marketplace games
        const isApproved = game.marketplaceData?.isApproved === true || 
                          game.marketplaceData?.approvalStatus === 'approved' ||
                          game.status === 'approved';
        console.log('🛒 MARKETPLACE: Game', game.title || game.id, 'approved:', isApproved, 'status:', game.status, 'marketplaceData:', game.marketplaceData);
        return isApproved;
      });
      
      console.log('🛒 MARKETPLACE: Approved marketplace games:', gamesData.length);
      console.log('🛒 MARKETPLACE: Sample game data:', gamesData[0]);
      
      // If no marketplace games, check regular games collection for fallback
      if (gamesData.length === 0) {
        console.log('🛒 MARKETPLACE: No marketplace games found, checking regular games collection...');
        const regularGamesSnapshot = await measuredGet(
          () => getDocs(collection(db, 'games')),
          'marketplace/regular_games',
          window.location.pathname
        );
        console.log('🛒 MARKETPLACE: Found', regularGamesSnapshot.size, 'games in regular collection');
        if (regularGamesSnapshot.size > 0) {
          console.log('🛒 MARKETPLACE: Sample regular game data:', regularGamesSnapshot.docs[0].data());
        }
        
        // Filter for marketplace games in regular collection
        const marketplaceGames = regularGamesSnapshot.docs.map(doc => {
          try {
            const gameData = doc.data();
            
            // Only include games that are marked for marketplace
            if (!gameData.isForMarketplace && !gameData.marketplaceData) {
              return null;
            }
            
            // Check if approved
            const isApproved = gameData.marketplaceData?.isApproved === true || 
                              gameData.marketplaceData?.approvalStatus === 'approved' ||
                              gameData.status === 'approved';
            
            if (!isApproved) {
              return null;
            }
            
            return {
              id: doc.id,
              title: gameData.title || `Game ${doc.id}`,
              description: gameData.description || 'A trivia game',
              creatorName: gameData.creatorName || 'Unknown',
              category: gameData.category || 'General',
              difficulty: gameData.difficulty || 'Medium',
              questions: '23', // Standard trivia game
              marketplaceData: {
                price: gameData.marketplaceData?.price || 20,
                salesCount: gameData.marketplaceData?.salesCount || 0,
                isApproved: true
              }
            };
          } catch (error) {
            console.error('Error processing game data for doc', doc.id, ':', error);
            return null;
          }
        }).filter(game => game !== null);
        
        console.log('🛒 MARKETPLACE: Found', marketplaceGames.length, 'marketplace games in regular collection');
        setGames(marketplaceGames);
      } else {
        setGames(gamesData);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading games:', error);
      setLoading(false);
    }
  };

  const loadPreviouslyPurchasedGames = async () => {
    try {
      const previouslyPurchased = new Set();
      
      // Check user's games collection
      const userGamesSnapshot = await getDocs(collection(db, 'users', user.uid, 'games'));
      userGamesSnapshot.forEach(doc => {
        const gameData = doc.data();
        if (gameData.originalGameId) {
          previouslyPurchased.add(gameData.originalGameId);
        }
      });
      
      // Check user's purchases collection
      const purchasesSnapshot = await getDocs(
        query(collection(db, 'userPurchases'), where('userId', '==', user.uid))
      );
      purchasesSnapshot.forEach(doc => {
        const purchaseData = doc.data();
        previouslyPurchased.add(purchaseData.gameId);
      });
      
      setPreviouslyPurchasedGames(previouslyPurchased);
    } catch (error) {
      console.error('Error loading previously purchased games:', error);
    }
  };

  const loadOfficialGames = async () => {
    try {
      setLoadingOfficialGames(true);
      console.log('📅 Loading official games for calendar...');
      
      // Get first and last day of current month
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
      
      console.log('📅 Querying games from', startDate, 'to', endDate);
      
      // Fetch all approved games and filter client-side (Firestore doesn't support range queries on strings easily)
      const allGamesSnapshot = await measuredGet(
        () => getDocs(collection(db, 'games', 'content', 'triviaGames')),
        'marketplace/official_games',
        window.location.pathname
      );
      
      const allGames = allGamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter for official games with gameDate in current month
      const official = allGames.filter(game => {
        const isOfficial = game.isLeagueSponsored === true;
        const isApproved = game.marketplaceData?.approvalStatus === 'approved' || 
                          game.marketplaceData?.isApproved === true ||
                          game.status === 'approved';
        const hasDate = game.gameDate && game.gameDate >= startDate && game.gameDate <= endDate;
        
        return isOfficial && isApproved && hasDate;
      });
      
      console.log('✅ Found', official.length, 'official games for current month');
      setOfficialGames(official);
    } catch (error) {
      console.error('Error loading official games:', error);
    } finally {
      setLoadingOfficialGames(false);
    }
  };

  // Map games to dates for calendar
  const gamesByDate = useMemo(() => {
    const map = {};
    officialGames.forEach(game => {
      if (game.gameDate) {
        map[game.gameDate] = game;
      }
    });
    return map;
  }, [officialGames]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isToday = (day) => {
    if (day === null) return false;
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handlePreview = (game) => {
    setPreviewGame(game);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewGame(null);
  };

  // Filter and sort games based on search and filter criteria
  const filteredGames = useMemo(() => {
    let filtered = games.filter(game => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        (game.title || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (game.creatorName || 'Unknown')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (game.description || '')?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Category filter
      const matchesCategory = selectedCategory === 'all' || 
        (game.category || 'General')?.toLowerCase() === selectedCategory.toLowerCase();
      
      // Price range filter (based on base price, not total)
      const gamePrice = game.marketplaceData?.price || PRICING.MIN_BASE_PRICE;
      let matchesPrice = true;
      if (priceRange === 'under-20') matchesPrice = gamePrice < 20;
      else if (priceRange === '20-30') matchesPrice = gamePrice >= 20 && gamePrice <= 30;
      else if (priceRange === 'over-30') matchesPrice = gamePrice > 30;
      
      return matchesSearch && matchesCategory && matchesPrice;
    });
    
    // Sort games
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'oldest':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'price-low':
          return (a.marketplaceData?.price || 0) - (b.marketplaceData?.price || 0);
        case 'price-high':
          return (b.marketplaceData?.price || 0) - (a.marketplaceData?.price || 0);
        case 'popular':
          return (b.marketplaceData?.salesCount || 0) - (a.marketplaceData?.salesCount || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [games, searchTerm, selectedCategory, sortBy, priceRange]);

  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = [...new Set(games.map(game => game.category || 'General').filter(Boolean))];
    return ['all', ...cats];
  }, [games]);

  // Get unique creators for browse section
  const creators = useMemo(() => {
    const creatorMap = new Map();
    games.forEach(game => {
      const creatorName = game.creatorName || 'Unknown';
      if (!creatorMap.has(creatorName)) {
        creatorMap.set(creatorName, {
          name: creatorName,
          gameCount: 0,
          totalSales: 0,
          games: []
        });
      }
      const creator = creatorMap.get(creatorName);
      creator.gameCount++;
      creator.totalSales += game.marketplaceData?.salesCount || 0;
      creator.games.push(game);
    });
    return Array.from(creatorMap.values()).sort((a, b) => b.gameCount - a.gameCount);
  }, [games]);

  // Get popular themes/categories
  const popularThemes = useMemo(() => {
    const themeMap = new Map();
    games.forEach(game => {
      const theme = game.category || 'General';
      if (!themeMap.has(theme)) {
        themeMap.set(theme, {
          name: theme,
          gameCount: 0,
          totalSales: 0,
          games: []
        });
      }
      const themeData = themeMap.get(theme);
      themeData.gameCount++;
      themeData.totalSales += game.marketplaceData?.salesCount || 0;
      themeData.games.push(game);
    });
    return Array.from(themeMap.values()).sort((a, b) => b.gameCount - a.gameCount);
  }, [games]);

  // Handle browsing by creator
  const handleBrowseByCreator = (creatorName) => {
    setSearchTerm('');
    setSelectedCategory('all');
    setPriceRange('all');
    setSortBy('newest');
    setViewMode('games');
    // Set search term to creator name to filter games
    setTimeout(() => setSearchTerm(creatorName), 100);
  };

  // Handle browsing by theme
  const handleBrowseByTheme = (themeName) => {
    setSearchTerm('');
    setSelectedCategory(themeName);
    setPriceRange('all');
    setSortBy('newest');
    setViewMode('games');
  };

  // Handle viewing all games
  const handleViewAllGames = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setPriceRange('all');
    setSortBy('newest');
    setViewMode('games');
  };

  const handlePurchase = async (game) => {
    if (!user) {
      alert('Please log in to purchase games');
      navigate('/login');
      return;
    }

    // Check if user has previously purchased this game
    if (previouslyPurchasedGames.has(game.id)) {
      const confirmRepurchase = window.confirm(
        `You have previously purchased "${game.title}".\n\n` +
        `This will be a new purchase at the current price.\n\n` +
        `Are you sure you want to re-purchase this game?`
      );
      
      if (!confirmRepurchase) {
        return;
      }
    }

    // Go directly to Stripe checkout - no MFA needed for marketplace purchases
    setPurchaseLoading(prev => ({ ...prev, [game.id]: true }));
    
    try {
      console.log('🎯 Creating checkout for game:', game.title);
      
      // Create Stripe checkout session for marketplace purchase with revenue split
      const createMarketplaceGameCheckout = httpsCallable(functions, 'createMarketplaceGameCheckout');
      const checkoutResult = await createMarketplaceGameCheckout({
        gameId: game.id
      });
      
      if (checkoutResult.data?.url) {
        // Redirect to Stripe checkout
        window.location.href = checkoutResult.data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      
      // Handle specific error cases with user-friendly messages
      let errorMessage = 'We couldn\'t process your purchase right now.';
      let errorTitle = 'Purchase Failed';
      
      if (error.code === 'unavailable' || error.message?.includes('network') || error.message?.includes('Failed to fetch')) {
        errorTitle = 'Connection Problem';
        errorMessage = 'Unable to connect to our payment server. Please check your internet connection and try again.';
      } else if (error.message?.includes('Creator payment account is not fully set up') || 
                 error.message?.includes('Creator has not set up payment account')) {
        errorTitle = 'Game Not Available';
        errorMessage = 'The creator of this game hasn\'t completed their payment setup yet. Please try a different game or contact support if you need this specific game.';
      } else if (error.message?.includes('Game is not approved for sale')) {
        errorTitle = 'Game Not Available';
        errorMessage = 'This game is not currently available for purchase. It may be pending approval or have been removed. Please try a different game.';
      } else if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        errorTitle = 'Access Denied';
        errorMessage = 'You don\'t have permission to purchase this game. Please make sure you\'re logged in and try again.';
      } else if (error.message?.includes('CORS')) {
        errorTitle = 'Connection Error';
        errorMessage = 'There was a connection issue. Please try again in a moment.';
      } else {
        errorMessage = 'Something went wrong while processing your purchase. Please try again, or contact support if the problem continues.';
      }
      
      // Use a more user-friendly alert with title and message
      window.alert(`${errorTitle}\n\n${errorMessage}`);
      
      setPurchaseLoading(prev => ({ ...prev, [game.id]: false }));
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading marketplace...</div>
      </div>
    );
  }

  return (
    <HostLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-4">🎮 Game Marketplace</h1>
              <p className="text-gray-400">
                {viewMode === 'calendar' 
                  ? 'Official Trivia Plug Games - One game for every day of the year'
                  : 'Browse and purchase trivia games from other creators'}
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  viewMode === 'calendar' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                📅 Official Games
              </button>
              <button
                onClick={() => setViewMode('browse')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  viewMode === 'browse' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Browse
              </button>
              <button
                onClick={() => setViewMode('games')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  viewMode === 'games' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Community Games
              </button>
            </div>
          </div>
        </div>

        {/* Calendar View - Default for Official Games */}
        {viewMode === 'calendar' && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handlePrevMonth}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    ←
                  </button>
                  <h2 className="text-2xl font-bold text-white">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h2>
                  <button
                    onClick={handleNextMonth}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    →
                  </button>
                </div>
                <button
                  onClick={() => setCurrentMonth(new Date())}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                >
                  Today
                </button>
              </div>

              {loadingOfficialGames ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                  <p className="mt-4 text-gray-400">Loading official games...</p>
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-2">
                  {/* Day headers */}
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center font-semibold text-gray-400 py-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar days */}
                  {getDaysInMonth(currentMonth).map((day, index) => {
                    if (day === null) {
                      return <div key={`empty-${index}`} className="aspect-square"></div>;
                    }
                    
                    const dateKey = formatDateKey(
                      currentMonth.getFullYear(),
                      currentMonth.getMonth(),
                      day
                    );
                    const game = gamesByDate[dateKey];
                    const isTodayDate = isToday(day);
                    const isPurchased = game && previouslyPurchasedGames.has(game.id);
                    
                    return (
                      <div
                        key={day}
                        className={`aspect-square bg-gray-700 rounded-lg p-2 border-2 ${
                          isTodayDate 
                            ? 'border-blue-500 bg-blue-900/20' 
                            : 'border-gray-600'
                        } ${game ? 'hover:bg-gray-600' : ''} transition-colors`}
                      >
                        <div className="flex flex-col h-full">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-semibold ${isTodayDate ? 'text-blue-400' : 'text-white'}`}>
                              {day}
                            </span>
                            {isTodayDate && (
                              <span className="text-xs text-blue-400 font-medium">Today</span>
                            )}
                          </div>
                          
                          {game ? (
                            <div className="flex-1 flex flex-col justify-between text-xs">
                              <div className="mb-1">
                                <div className="text-white font-medium truncate" title={game.title}>
                                  {game.title}
                                </div>
                                <div className="text-green-400 font-semibold">
                                  ${(game.marketplaceData?.price || PRICING.MIN_BASE_PRICE) + PRICING.LAUNCH_FEE}
                                </div>
                              </div>
                              
                              {isPurchased && (
                                <div className="mb-1 text-green-400 text-xs">✓ Purchased</div>
                              )}
                              
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={() => handlePurchase(game)}
                                  disabled={purchaseLoading[game.id] || isPurchased}
                                  className={`w-full px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    isPurchased
                                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                                  }`}
                                >
                                  {purchaseLoading[game.id] ? 'Processing...' : 
                                   isPurchased ? 'Purchased' : 'Buy'}
                                </button>
                                <button
                                  onClick={() => handlePreview(game)}
                                  className="w-full px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors"
                                >
                                  Preview
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex-1 text-gray-500 text-xs flex items-center justify-center">
                              No game
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Browse Section */}
        {viewMode === 'browse' && (
          <div className="space-y-8">
            {/* Browse by Creators */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">👥 Browse by Creators</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {creators.slice(0, 6).map((creator) => (
                  <button
                    key={creator.name}
                    onClick={() => handleBrowseByCreator(creator.name)}
                    className="bg-gray-700 hover:bg-gray-600 rounded-lg p-4 text-left transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-white">{creator.name}</h3>
                      <span className="text-blue-400 text-sm">{creator.gameCount} games</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      {creator.totalSales} total sales
                    </p>
                  </button>
                ))}
              </div>
              {creators.length > 6 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewAllGames}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    View all {creators.length} creators →
                  </button>
                </div>
              )}
            </div>

            {/* Browse by Themes */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">🎨 Browse by Themes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {popularThemes.slice(0, 6).map((theme) => (
                  <button
                    key={theme.name}
                    onClick={() => handleBrowseByTheme(theme.name)}
                    className="bg-gray-700 hover:bg-gray-600 rounded-lg p-4 text-left transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-white">{theme.name}</h3>
                      <span className="text-green-400 text-sm">{theme.gameCount} games</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      {theme.totalSales} total sales
                    </p>
                  </button>
                ))}
              </div>
              {popularThemes.length > 6 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewAllGames}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    View all {popularThemes.length} themes →
                  </button>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">⚡ Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={handleViewAllGames}
                  className="bg-blue-600 hover:bg-blue-700 rounded-lg p-4 text-center transition-colors"
                >
                  <div className="text-2xl mb-2">🎮</div>
                  <h3 className="font-semibold text-white">View All Games</h3>
                  <p className="text-blue-200 text-sm">Browse all {games.length} games</p>
                </button>
                <button
                  onClick={() => {
                    setViewMode('games');
                    setSortBy('popular');
                  }}
                  className="bg-green-600 hover:bg-green-700 rounded-lg p-4 text-center transition-colors"
                >
                  <div className="text-2xl mb-2">🔥</div>
                  <h3 className="font-semibold text-white">Popular Games</h3>
                  <p className="text-green-200 text-sm">See what's trending</p>
                </button>
                <button
                  onClick={() => {
                    setViewMode('games');
                    setSortBy('newest');
                  }}
                  className="bg-purple-600 hover:bg-purple-700 rounded-lg p-4 text-center transition-colors"
                >
                  <div className="text-2xl mb-2">✨</div>
                  <h3 className="font-semibold text-white">Newest Games</h3>
                  <p className="text-purple-200 text-sm">Fresh content</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter Section - Only show in games view */}
        {viewMode === 'games' && (
          <div className="mb-8 bg-gray-800 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Search Games</label>
                <input
                  type="text"
                  placeholder="Search by title, creator, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Price Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Price Range</label>
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Prices</option>
                  <option value="under-20">Under $20</option>
                  <option value="20-30">$20 - $30</option>
                  <option value="over-30">Over $30</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="popular">Most Popular</option>
                  <option value="title">Title A-Z</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                Showing {filteredGames.length} of {games.length} games
              </p>
            </div>
          </div>
        )}

        {/* Games Grid Section - Only show in games view */}
        {viewMode === 'games' && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredGames.map((game) => (
              <div key={game.id} className="bg-gray-800 rounded-lg p-6 hover:bg-gray-700 transition-all duration-200 hover:shadow-lg">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{game.title}</h3>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-3">{game.description}</p>
                </div>
                
                <div className="mb-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Creator:</span>
                    <span className="text-gray-200 text-sm font-medium">{game.creatorName || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Category:</span>
                    <span className="text-gray-200 text-sm font-medium">{game.category || 'General'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Difficulty:</span>
                    <span className="text-gray-200 text-sm font-medium">{game.difficulty || 'Medium'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Sales:</span>
                    <span className="text-gray-200 text-sm font-medium">{game.marketplaceData?.salesCount || 0}</span>
                  </div>
                </div>

                {previouslyPurchasedGames.has(game.id) && (
                  <div className="mb-4 p-2 bg-yellow-900/20 border border-yellow-600/30 text-yellow-200 rounded-lg text-xs text-center">
                    ⚠️ Previously purchased
                  </div>
                )}

                <div className="pt-4 border-t border-gray-700">
                  <div className="mb-3">
                    <div className="flex items-baseline justify-between mb-1">
                      <div>
                        <div className="text-3xl font-bold text-white">
                          ${(game.marketplaceData?.price || PRICING.MIN_BASE_PRICE) + PRICING.LAUNCH_FEE}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          ${game.marketplaceData?.price || PRICING.MIN_BASE_PRICE} game + ${PRICING.LAUNCH_FEE} launch fee
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePurchase(game)}
                    disabled={purchaseLoading[game.id]}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    {purchaseLoading[game.id] ? 'Processing...' : 
                     previouslyPurchasedGames.has(game.id) ? 'Re-purchase' : 'Purchase'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredGames.length === 0 && games.length > 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-white mb-2">No games match your filters</h3>
              <p className="text-gray-400 mb-4">Try adjusting your search terms or filters</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setPriceRange('all');
                  setSortBy('newest');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          )}
          </>
        )}

        {games.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-xl font-semibold text-white mb-2">No games available in the marketplace</h3>
            <p className="text-gray-400 mb-4">Be the first to submit a game to the marketplace!</p>
            <button
              onClick={() => navigate('/add-game')}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Create Your First Game
            </button>
          </div>
        )}

        {/* Preview Modal */}
        {showPreviewModal && previewGame && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-white">{previewGame.title}</h2>
                  <button
                    onClick={handleClosePreview}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
                    <p className="text-gray-300">{previewGame.description || 'No description available'}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400">Creator:</span>
                      <span className="text-white ml-2">{previewGame.creatorName || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Category:</span>
                      <span className="text-white ml-2">{previewGame.category || 'General'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Difficulty:</span>
                      <span className="text-white ml-2">{previewGame.difficulty || 'Medium'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Sales:</span>
                      <span className="text-white ml-2">{previewGame.marketplaceData?.salesCount || 0}</span>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-700">
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-white mb-1">
                        ${(previewGame.marketplaceData?.price || PRICING.MIN_BASE_PRICE) + PRICING.LAUNCH_FEE}
                      </div>
                      <div className="text-xs text-gray-400">
                        ${previewGame.marketplaceData?.price || PRICING.MIN_BASE_PRICE} game + ${PRICING.LAUNCH_FEE} launch fee
                      </div>
                    </div>
                    
                    {previouslyPurchasedGames.has(previewGame.id) && (
                      <div className="mb-4 p-2 bg-yellow-900/20 border border-yellow-600/30 text-yellow-200 rounded-lg text-sm text-center">
                        ⚠️ Previously purchased
                      </div>
                    )}
                    
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          handleClosePreview();
                          handlePurchase(previewGame);
                        }}
                        disabled={purchaseLoading[previewGame.id] || previouslyPurchasedGames.has(previewGame.id)}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                          previouslyPurchasedGames.has(previewGame.id)
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {purchaseLoading[previewGame.id] ? 'Processing...' : 
                         previouslyPurchasedGames.has(previewGame.id) ? 'Already Purchased' : 'Purchase'}
                      </button>
                      <button
                        onClick={handleClosePreview}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </HostLayout>
  );
};

export default Marketplace; 